/**
 * Tenant entitlements - what the customer has actually purchased.
 *
 * Distinct from feature flags (which we toggle per environment) and from IAM grants (which say
 * what an individual user may do). An entitlement says the tenant bought the feature, so it is
 * the correct gate for a paid add-on.
 *
 * The BFF resolves these from IAM-DS using its own service identity, so the answer is the same
 * for every user of a tenant regardless of their own IAM roles.
 */

/**
 * Feature names as returned by GET /bffapi/v1/Entitlements. These are a stable contract with the
 * BFF - the underlying IAM feature identifiers differ per environment and stay server-side.
 */
export const ENTITLEMENT_FEATURES = Object.freeze({
    DIRECT_SUBMISSION: 'directSubmission',
});

/**
 * Whether the tenant is entitled to a feature.
 *
 * Fails closed: an unresolved fetch, a failed fetch or an unknown feature all report false, so a
 * paid feature is never shown by accident.
 *
 * @param {object} entitlements - The entitlements sub-state, i.e. state.authentication.entitlements
 * @param {string} feature - A value from ENTITLEMENT_FEATURES
 * @returns {boolean} True only when the tenant is explicitly entitled to the feature
 */
export const hasEntitlement = (entitlements, feature) =>
    entitlements?.features?.[feature] === true;

export default {
    ENTITLEMENT_FEATURES,
    hasEntitlement,
};
